import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { ArrowDown2 } from 'iconsax-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import {
  ITransactionForCashReportResponse,
  IMonthlyReportProps,
  IComparePeriodsResponse,
} from '../../apis/models/transactions';
import CashReportDocument from './pdf/CashReportDocument';

type TFunc = (key: string) => string;

interface ReportDownloadButtonProps {
  cashReport: ITransactionForCashReportResponse | undefined;
  summaryData: IMonthlyReportProps | undefined;
  dateRange: { start: Date; end: Date };
  schoolName: string;
  userName: string;
  resolveFeeType: (enumKey: string) => string;
  /** Active tab on the cash report page: 0 = incomes, 1 = expenses, 2 = balance. */
  currentTab: number;
  disabled?: boolean;
  /** When set (compare mode on + comparison data loaded), the PDF renders the comparison view instead. */
  comparison?: { data: IComparePeriodsResponse; labelA: string; labelB: string };
}

const ReportDownloadButton: React.FC<ReportDownloadButtonProps> = ({
  cashReport,
  summaryData,
  dateRange,
  schoolName,
  userName,
  resolveFeeType,
  currentTab,
  disabled = false,
  comparison,
}) => {
  const { t: tRaw } = useTranslation();
  const t = tRaw as unknown as TFunc;
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // Scope the download to the active tab: incomes tab → incomes only,
  // expenses tab → expenses only, balance tab → both (unchanged).
  const includeIncomes = currentTab === 0 || currentTab === 2;
  const includeExpenses = currentTab === 1 || currentTab === 2;

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) return;
    setOpen(false);
  };

  const handleExportCSV = () => {
    if (!cashReport) return;

    // Wrap every value in double-quotes and escape any internal quotes,
    // so commas inside dates (e.g. "5 May, 10:30") don't break columns.
    const q = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const row = (...cells: (string | number)[]) => cells.map(q).join(',');

    const incomeHeaders = row(
      t('tables.fee_type'),
      t('tables.amount'),
      t('tables.date'),
      t('tables.names'),
      t('tables.description'),
      t('tables.employee_name')
    );
    const incomeRows = cashReport.incomes.map((r) =>
      row(r.feeType, r.fee_amount, r.date_sold, r.names, r.description ?? '', r.cashier ?? '')
    );

    const expenseHeaders = row(
      t('tables.fee_type'),
      t('tables.amount'),
      t('tables.date'),
      t('tables.beneficiary_name'),
      t('tables.description'),
      t('tables.employee_name'),
      t('tables.authoriser_name')
    );
    const expenseRows = cashReport.expenses.map((r) =>
      row(
        r.feeCode,
        r.fee_amount,
        r.date,
        r.beneficiary_name,
        r.description ?? '',
        r.cashier ?? '',
        r.authorizer ?? ''
      )
    );

    const sections: string[] = [];
    if (includeIncomes) {
      sections.push(`${t('reports_breakdown.incomes')}`, incomeHeaders, ...incomeRows);
    }
    if (includeExpenses) {
      if (sections.length) sections.push('');
      sections.push(`${t('reports_breakdown.expenses')}`, expenseHeaders, ...expenseRows);
    }

    const csv = sections.join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cash-report-${dateRange.start.toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
  };

  if (!cashReport && !summaryData) return null;

  const pdfDocument = (
    <CashReportDocument
      cashReport={cashReport}
      summaryData={summaryData}
      dateRange={dateRange}
      schoolName={schoolName}
      resolveFeeType={resolveFeeType}
      includeIncomes={includeIncomes}
      includeExpenses={includeExpenses}
      comparison={comparison}
      t={t}
    />
  );

  return (
    <>
      <ButtonGroup variant="contained" ref={anchorRef} disabled={disabled}>
        <PDFDownloadLink
          document={pdfDocument}
          fileName={
            comparison
              ? `comparison-report-${dateRange.start.toISOString().slice(0, 10)}.pdf`
              : `cash-report-${dateRange.start.toISOString().slice(0, 10)}.pdf`
          }
          style={{ textDecoration: 'none' }}
        >
          {({ loading }) => (
            <Button disabled={loading || disabled}>
              {loading ? '...' : comparison ? t('report.download_comparison_pdf') : t('report.download_pdf')}
            </Button>
          )}
        </PDFDownloadLink>
        <Button size="small" onClick={handleToggle}>
          <ArrowDown2 size={16} />
        </Button>
      </ButtonGroup>
      <Popper open={open} anchorEl={anchorRef.current} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  <MenuItem onClick={handleExportCSV}>{t('report.export_csv')}</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default ReportDownloadButton;
